import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Trash2, Upload } from 'react-feather';
import { connect } from 'react-redux';
import request from 'utils/request_ajax.js';
import { Icon } from 'ui';
import { routes as ROUTES } from 'constants/config';
import { useFeatureFlags } from 'components/shared/hooks';
import { UniversalUploader } from 'components/shared/UniversalUploader';
import * as selectors from 'selectors/pathBuilder';
import CertificationBadge from 'components/CertificationBadge';
import CustomBadgeModal from 'components/PathBuilder/Sidebar/CustomBadgeModal';
import ImageLibraryAddDialog from 'components/shared/ImageLibraryAddDialog';
import RemoveBadgeModal from 'components/PathBuilder/Sidebar/RemoveBadgeModal';
import fileStackImageUpload from 'misc/imageUpload';
import * as actions from '../../../actions/pathBuilder';
import Section, { SectionHeader } from './Section';
import BadgeEditorModal from './BadgeEditorModal';
import CertificationShareToggle from './CertificationShareToggle';

const requestUpdateCertification = (pathId, certification) =>
  request.putJSON(ROUTES.PATH(pathId), { certification });

const requestRemoveBadge = (imageId, image) =>
  request.delete(ROUTES.IMAGE(imageId), { image });

const shouldShowActionButtons = ({ pathId, userCanEditContent, isArchived }) =>
  pathId && userCanEditContent && !isArchived;

const Linkbutton = ({
  isLoading = false,
  icon = '',
  children = '',
  onClick = () => {},
}) => (
  <button
    type="button"
    disabled={isLoading}
    className="u-linkBtn fz-base fw-n td-n"
    onClick={onClick}
  >
    {isLoading && <span className="Spinner mr-4" />}
    {!isLoading && (
      <>
        <Icon name={icon} /> {children}
      </>
    )}
  </button>
);

const Badge = ({
  pathId,
  updatePath,
  isArchived,
  userCanEditContent,
  certification: { id, iconName, backgroundColor, foregroundColor, image },
  features,
  permissions,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCustomBadgeModalOpen, setIsCustomBadgeModalOpen] = useState(false);
  const [addToLibraryVisible, setAddToLibraryVisible] = useState(false);
  const [isRemoveBadgeModalOpen, setIsRemoveBadgeModalOpen] = useState(false);
  const [isRemoveBadgeLoading, setIsRemoveBadgeLoading] = useState(false);
  const [universalUploaderModalVisible, setUniversalUploaderModalVisible] =
    useState(false);

  const hasFileUploadFF = useFeatureFlags([
    'learning-file-stack-upload-replacement',
  ]);

  const showActionButtons = shouldShowActionButtons({
    pathId,
    userCanEditContent,
    isArchived,
  });

  const updateCertification = certification => {
    setIsLoading(true);
    setHasError(false);
    return requestUpdateCertification(pathId, certification)
      .then(response => {
        if (!response.success) {
          setIsLoading(false);
          setHasError(true);
          console.error(response);
          return response;
        }

        updatePath({ certification: response.certification });
        setIsLoading(false);
        return response;
      })
      .catch(e => {
        setIsLoading(false);
        setHasError(true);
        console.error(e);
      });
  };

  const closeUniversalUploaderModal = () => {
    if (universalUploaderModalVisible) {
      setUniversalUploaderModalVisible(false);
    }
  };

  const toggleUniversalUploader = () => {
    setUniversalUploaderModalVisible(!universalUploaderModalVisible);
  };

  const toggleAddToLibrary = () => {
    setAddToLibraryVisible(!addToLibraryVisible);
  };

  const updateBadge = badge => {
    updateCertification({
      id,
      iconName: badge.iconName,
      foregroundColor: badge.foregroundColor,
      backgroundColor: badge.backgroundColor,
    }).then(response => {
      if (response.success && isEditorOpen) {
        setIsEditorOpen(false);
      }
    });
  };

  const toggleCustomBadgeModal = () => {
    setIsCustomBadgeModalOpen(!isCustomBadgeModalOpen);
  };

  const handleCustomBadgeUpload = () => {
    const associatedImageParams = {
      content_id: id,
      content_type: 'Certification',
      image_type: 'badge',
    };

    if (hasFileUploadFF) {
      toggleUniversalUploader();
      toggleCustomBadgeModal();
    } else {
      toggleCustomBadgeModal();

      fileStackImageUpload(associatedImageParams)
        .then(response => {
          updateBadge({
            id,
            iconName,
            foregroundColor,
            backgroundColor,
            image: response,
          });
          toggleAddToLibrary();
        })
        .catch(e => {
          console.error(e);
          if (addToLibraryVisible) {
            toggleAddToLibrary();
          }
        });
    }
  };

  const saveBssUpload = newUpload => {
    return new Promise(async (resolve, reject) => {
      const { bssUploadId } = newUpload;

      try {
        const uploadImage = await request.postJSON(ROUTES.IMAGES, {
          image: {
            bss_upload_id: bssUploadId,
            content_id: id,
            content_type: 'Certification',
            image_type: 'badge',
          },
        });
        await updateBadge({
          id,
          iconName,
          foregroundColor,
          backgroundColor,
          uploadImage,
        });
        closeUniversalUploaderModal();
        toggleAddToLibrary();
        resolve();
      } catch (e) {
        reject(new Error(e));
      }
    });
  };

  const editBadge = () => setIsEditorOpen(!isEditorOpen);

  const showRemoveBadgeModal = () => {
    setIsRemoveBadgeModalOpen(true);
  };

  const closeRemoveBadgeModal = () => {
    setIsRemoveBadgeModalOpen(false);
  };

  const handleRemoveBadge = () => {
    setIsRemoveBadgeLoading(true);
    return requestRemoveBadge(image.id, {
      content_id: id,
      image_type: 'badge',
      content_type: 'Certification',
    })
      .then(() => {
        setIsRemoveBadgeLoading(false);
        updateCertification({
          id,
          image,
        });
        closeRemoveBadgeModal();
      })
      .catch(e => {
        setIsRemoveBadgeLoading(false);
        console.error(e);
      });
  };

  return (
    <Section>
      <SectionHeader className="d-f jc-sb" htmlFor="pathDescription">
        {t('certifications.certification_builder.sidebar.settings_title')}
      </SectionHeader>
      {showActionButtons && <CertificationShareToggle />}
      <div className="d-f ai-c jc-c mt-12">
        <button
          type="button"
          className="u-resetBtn d-f"
          disabled={!showActionButtons || image}
          onClick={setIsEditorOpen}
        >
          <CertificationBadge
            image={image}
            iconName={iconName}
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
          />
        </button>
      </div>
      {showActionButtons && (
        <div className="d-f fd-c ai-fs jc-sb mt-12">
          {!image && (
            <Linkbutton onClick={editBadge} icon="edit" className="mb-4">
              {t(
                'certifications.certification_builder.sidebar.badge_edit_button',
              )}
            </Linkbutton>
          )}
          {
            <button
              className="u-linkBtn mt-4 d-f"
              type="button"
              onClick={toggleCustomBadgeModal}
            >
              <Upload size={14} className="mt-2 mr-4" />
              {t(
                'certifications.certification_builder.sidebar.upload_custom_badge_button',
              )}
            </button>
          }
          {image && (
            <button
              className="u-linkBtn mt-4 d-f"
              type="button"
              onClick={showRemoveBadgeModal}
            >
              <Trash2 size={14} className="mt-2 mr-4" />
              {t(
                'certifications.certification_builder.sidebar.remove_badge_button',
              )}
            </button>
          )}
        </div>
      )}
      {universalUploaderModalVisible && (
        <UniversalUploader
          accept={['image/*']}
          displayInModal
          modalShown={universalUploaderModalVisible}
          onModalDismiss={closeUniversalUploaderModal}
          onComplete={saveBssUpload}
          helperText={t('universal_uploader.image_library_helper')}
          deleteUploadRecord={false}
        />
      )}
      <ImageLibraryAddDialog
        canManageContentLibrary={permissions.can_manage_content_library}
        image={image}
        visible={image && addToLibraryVisible}
        toggleVisible={toggleAddToLibrary}
      />
      <CustomBadgeModal
        shown={isCustomBadgeModalOpen}
        toggleModal={toggleCustomBadgeModal}
        toggleUploadModal={handleCustomBadgeUpload}
      />
      <BadgeEditorModal
        hasError={hasError}
        isLoading={isLoading}
        iconName={iconName}
        backgroundColor={backgroundColor}
        foregroundColor={foregroundColor}
        shown={isEditorOpen}
        toggleModal={editBadge}
        updateBadge={updateBadge}
      />
      <RemoveBadgeModal
        shown={isRemoveBadgeModalOpen}
        closeModal={closeRemoveBadgeModal}
        removeBadge={handleRemoveBadge}
        isRemoveBadgeLoading={isRemoveBadgeLoading}
      />
    </Section>
  );
};

Badge.propTypes = {
  pathId: PropTypes.number,
  updatePath: PropTypes.func,
  isArchived: PropTypes.bool,
  userCanEditContent: PropTypes.bool,
  certification: PropTypes.shape({
    id: PropTypes.number,
    iconName: PropTypes.string,
    backgroundColor: PropTypes.string,
    foregroundColor: PropTypes.string,
  }),
};

const mapStateToProps = ({ pathBuilder, ...state }) => {
  return {
    pathId: pathBuilder.id,
    certification: pathBuilder.certification || {},
    isArchived: selectors.isArchived(pathBuilder),
    userCanEditContent: pathBuilder.permissions.editPath,
    permissions: state.meta.permissions,
    features: {
      allowImageLibrary: state.meta.features.allowImageLibrary,
    },
  };
};

const mapDispatchToProps = {
  updatePath: actions.updatePath,
};

export default connect(mapStateToProps, mapDispatchToProps)(Badge);
